/**
 * Vue权限插件
 * 提供权限指令、混入等功能
 */

import { permissionManager, permissionState, PERMISSIONS, ROLES } from '@/utils/permission';

/**
 * 权限指令
 * 用法: v-permission="'device.view'" 或 v-permission="['device.view', 'device.edit']"
 */
const permissionDirective = {
  bind(el, binding) {
    const permission = binding.value;
    if (!checkPermission(permission)) {
      el.style.display = 'none';
    }
  },
  update(el, binding) {
    const permission = binding.value;
    if (!checkPermission(permission)) {
      el.style.display = 'none';
    } else {
      el.style.display = '';
    }
  }
};

/**
 * 角色指令
 * 用法: v-role="0" 或 v-role="[0, 1]"
 */
const roleDirective = {
  bind(el, binding) {
    const role = binding.value;
    if (!checkRole(role)) {
      el.style.display = 'none';
    }
  },
  update(el, binding) {
    const role = binding.value;
    if (!checkRole(role)) {
      el.style.display = 'none';
    } else {
      el.style.display = '';
    }
  }
};

/**
 * 检查权限
 * @param {string|Array} permission 权限名称或权限列表
 * @returns {boolean} 是否拥有权限
 */
function checkPermission(permission) {
  if (!permission) return true;
  
  if (Array.isArray(permission)) {
    return permissionManager.hasAllPermissions(permission);
  }
  
  return permissionManager.hasPermission(permission);
}

/**
 * 检查角色
 * @param {number|Array} role 角色ID或角色列表
 * @returns {boolean} 是否拥有角色
 */
function checkRole(role) {
  if (role === undefined || role === null) return true;
  
  if (Array.isArray(role)) {
    return role.some(r => permissionManager.hasRole(r));
  }
  
  return permissionManager.hasRole(role);
}

/**
 * 权限混入
 * 为组件提供权限相关的方法和计算属性
 */
const permissionMixin = {
  computed: {
    $permissionVersion() {
      return permissionState.version;
    },
    $permissionReady() {
      return permissionState.ready;
    },
    // 当前用户权限
    $permissions() {
      void permissionState.version;
      return permissionManager.getUserPermissions();
    },
    
    // 当前用户角色
    $userRole() {
      return permissionManager.getUserRole();
    },
    
    // 是否为超级管理员
    $isSuperAdmin() {
      return permissionManager.isSuperAdmin();
    },
    
    // 是否为管理员
    $isAdmin() {
      return permissionManager.isAdmin();
    }
  },
  
  methods: {
    /**
     * 检查权限
     * @param {string|Array} permission 权限名称或权限列表
     * @returns {boolean} 是否拥有权限
     */
    $hasPermission(permission) {
      return permissionManager.hasPermission(permission);
    },
    
    /**
     * 检查所有权限
     * @param {Array} permissions 权限列表
     * @returns {boolean} 是否拥有所有权限
     */
    $hasAllPermissions(permissions) {
      return permissionManager.hasAllPermissions(permissions);
    },
    
    /**
     * 检查任意权限
     * @param {Array} permissions 权限列表
     * @returns {boolean} 是否拥有任意一个权限
     */
    $hasAnyPermission(permissions) {
      return permissionManager.hasAnyPermission(permissions);
    },
    
    /**
     * 检查角色
     * @param {number|Array} role 角色ID或角色列表
     * @returns {boolean} 是否拥有角色
     */
    $hasRole(role) {
      if (Array.isArray(role)) {
        return role.some(r => permissionManager.hasRole(r));
      }
      return permissionManager.hasRole(role);
    },
    
    /**
     * 刷新权限
     */
    $refreshPermissions() {
      return permissionManager.reloadUser();
    },
    
    /**
     * 清理权限（退出登录时使用）
     */
    $clearPermissions() {
      permissionManager.clearUser();
    },

    /**
     * 列表/订单为空时：仅门店，或子账号且 deviceScopeCount===0 时提示；
     * 主账号管理员不提示，避免“暂无订单”被误报为未关联设备。
     * @param {Function} [retryFn] 刷新权限后的重试回调
     * @returns {boolean} 是否已触发重试或提示
     */
    warnIfEmptyDeviceScope(retryFn) {
      const roleId = parseInt(permissionState.roleId, 10);
      const scopeCount = permissionState.deviceScopeCount;
      const isSubaccount = !!permissionState.isSubaccount;

      // 主账号（含管理员）：不弹子账号设备范围提示
      if (roleId !== ROLES.STORE && !isSubaccount) {
        return false;
      }
      // 子账号仅在服务端设备范围为 0 时提示；有范围但无订单则静默
      if (isSubaccount && scopeCount !== 0) {
        return false;
      }

      if (
        scopeCount === 0 &&
        this.scopeRetryAttempt < 1 &&
        typeof this.$refreshPermissions === 'function' &&
        typeof retryFn === 'function'
      ) {
        this.scopeRetryAttempt += 1;
        this.$refreshPermissions().then(retryFn);
        return true;
      }

      if (roleId === ROLES.STORE) {
        this.$message.warning(this.$t('permission.store.emptyDeviceList'));
        return true;
      }
      if (isSubaccount && scopeCount === 0) {
        this.$message.warning(this.$t('permission.subaccount.emptyDeviceList'));
        return true;
      }
      return false;
    }
  }
};

/**
 * Vue权限插件
 */
const PermissionPlugin = {
  install(Vue, options = {}) {
    // 注册权限指令
    Vue.directive('permission', permissionDirective);
    
    // 注册角色指令
    Vue.directive('role', roleDirective);
    
    // 注册权限混入
    Vue.mixin(permissionMixin);
    
    // 在Vue原型上添加权限管理器
    Vue.prototype.$permission = permissionManager;
    
    // 在Vue原型上添加权限常量
    Vue.prototype.$PERMISSIONS = PERMISSIONS;
    Vue.prototype.$ROLES = ROLES;
    
    // 添加全局方法
    Vue.hasPermission = permissionManager.hasPermission.bind(permissionManager);
    Vue.hasRole = permissionManager.hasRole.bind(permissionManager);
    
    // 添加全局属性
    Vue.prototype.$permissionManager = permissionManager;
    Vue.prototype.$permissionState = permissionState;
    Vue.prototype.$getPermissionDebugInfo =
      permissionManager.getPermissionDebugInfo.bind(permissionManager);
  }
};

export default PermissionPlugin;
export { permissionMixin, PERMISSIONS, ROLES };
